import { Fragment, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import ChannelSelector from "./ChannelSelector";
import DeliveryModeSection from "./DeliveryModeSection";
import OrganizationsPreferencesList, {
  OrganizationPreferenceRow,
} from "./OrganizationsPreferencesList";
import CustomizeOrganizationModal from "./CustomizeOrganizationModal";
import CommunicationSetupModal from "./CommunicationSetupModal";
import FirstVisitSetupCard from "./FirstVisitSetupCard";
import PhoneVerificationPopup from "../popups/phoneVerificationPopup";
import SubHeader from "../global/SubHeader";
import RouteLeavingGuard from "../../pages/Notifications/RouteLeaving";
import {
  CommunicationChannel,
  DEFAULT_CHANNELS,
  DEFAULT_US_TIMEZONE,
  DeliveryMode,
  SummaryFrequency,
  WeekDay,
} from "../../constants/communicationConstants";
import {
  useCommunicationPreferencesOverview,
  useMarkCommunicationSetupComplete,
  useOrganizationPreference,
  useRestoreOrganizationDefaults,
  useSaveCommunicationDefaults,
  useSaveOrganizationPreference,
  useSetExternalNotifications,
} from "../../services/utils/custom hooks/Communication/useCommunicationPreferences";
import useProfile from "../../services/utils/custom hooks/useProfile";
import { usePhoneVerificationStatus } from "../../services/utils/custom hooks/PhoneVerification/usePhoneVerification";
import toastMessages from "../../services/utils/helpers/toastMessage";
import NotificationSettingsTableLoadingIndicator from "../global/LoadingIndicators/NotificationsSettingsTableLoadingIndicator";
import { PreferenceSchedule } from "../../services/models/communication/communicationPreferences.model";
import {
  getApiErrorMessage,
  scheduleFromApi,
  scheduleFromDefaults,
  toSaveDefaultsPayload,
  toSaveOrganizationPayload,
} from "../../utils/helpers/communicationPreferencesHelpers";

function cloneSchedule(values: PreferenceSchedule): PreferenceSchedule {
  return {
    channels: [...values.channels],
    deliveryMode: values.deliveryMode,
    summaryTime: values.summaryTime,
    summaryFrequency: values.summaryFrequency,
    summaryDayOfWeek: values.summaryDayOfWeek,
    timezone: values.timezone,
  };
}

function schedulesEqual(
  a: PreferenceSchedule,
  b: PreferenceSchedule
): boolean {
  return (
    a.deliveryMode === b.deliveryMode &&
    a.summaryTime === b.summaryTime &&
    a.summaryFrequency === b.summaryFrequency &&
    a.summaryDayOfWeek === b.summaryDayOfWeek &&
    a.timezone === b.timezone &&
    a.channels.length === b.channels.length &&
    a.channels.every((channel, index) => channel === b.channels[index])
  );
}

function externalMapsEqual(
  a: Record<string, boolean>,
  b: Record<string, boolean>
): boolean {
  const keys = Object.keys(a).concat(
    Object.keys(b).filter((key) => !(key in a))
  );
  return keys.every((key) => Boolean(a[key]) === Boolean(b[key]));
}

function MyPreferencesForm(): JSX.Element {
  const history = useHistory();
  const location = useLocation<{ openCommSetup?: boolean; focusSetup?: boolean }>();
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();
  const { data: phoneStatus } = usePhoneVerificationStatus();
  const { data: overview, isLoading } = useCommunicationPreferencesOverview();
  const saveDefaultsMutation = useSaveCommunicationDefaults();
  const completeMutation = useMarkCommunicationSetupComplete();
  const setExternalMutation = useSetExternalNotifications();
  const saveOrgMutation = useSaveOrganizationPreference();
  const restoreOrgMutation = useRestoreOrganizationDefaults();

  const [channels, setChannels] = useState<CommunicationChannel[]>([
    ...DEFAULT_CHANNELS,
  ]);
  const [deliveryMode, setDeliveryMode] = useState<DeliveryMode>(
    DeliveryMode.REAL_TIME
  );
  const [summaryTime, setSummaryTime] = useState("08:00");
  const [summaryFrequency, setSummaryFrequency] = useState<SummaryFrequency>(
    SummaryFrequency.DAILY
  );
  const [summaryDayOfWeek, setSummaryDayOfWeek] = useState<WeekDay>(
    WeekDay.MONDAY
  );
  const [timezone, setTimezone] = useState<string>(DEFAULT_US_TIMEZONE);
  const [initialized, setInitialized] = useState(false);
  const [showSetupCard, setShowSetupCard] = useState(false);
  const [showEditor, setShowEditor] = useState(true);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [customizingOrgId, setCustomizingOrgId] = useState<string | null>(null);
  const [modalDraft, setModalDraft] = useState<PreferenceSchedule | null>(null);
  const [modalUseDefaults, setModalUseDefaults] = useState(false);
  const [modalHydrated, setModalHydrated] = useState(false);
  const [orgExternalById, setOrgExternalById] = useState<
    Record<string, boolean>
  >({});
  const [baselineSchedule, setBaselineSchedule] =
    useState<PreferenceSchedule | null>(null);
  const [baselineOrgExternal, setBaselineOrgExternal] = useState<
    Record<string, boolean>
  >({});
  const [isSavingAll, setIsSavingAll] = useState(false);

  const { data: orgDetail, isLoading: orgDetailLoading } =
    useOrganizationPreference(customizingOrgId);

  // Match Profile page: include primaryPhone for unverified numbers.
  const displayPhone =
    phoneStatus?.mobilePhone ||
    profile?.user?.mobilePhone ||
    phoneStatus?.primaryPhone ||
    profile?.user?.primaryPhone ||
    "";

  useEffect(() => {
    if (overview && !initialized) {
      const schedule = scheduleFromDefaults(overview.defaults);
      setChannels(schedule.channels);
      setDeliveryMode(schedule.deliveryMode);
      setSummaryTime(schedule.summaryTime);
      setSummaryFrequency(schedule.summaryFrequency);
      setSummaryDayOfWeek(schedule.summaryDayOfWeek);
      setTimezone(schedule.timezone);

      const externalMap: Record<string, boolean> = {};
      overview.organizations.forEach((org) => {
        externalMap[String(org.organizationId)] =
          org.externalNotificationsEnabled;
      });
      setOrgExternalById(externalMap);
      setBaselineSchedule(cloneSchedule(schedule));
      setBaselineOrgExternal({ ...externalMap });
      setInitialized(true);
    }
  }, [overview, initialized]);

  useEffect(() => {
    if (!initialized) return;

    const shouldOpenSetup =
      location.state?.openCommSetup || location.state?.focusSetup;

    if (shouldOpenSetup) {
      // Explore CTA: Stay informed landing + guided setup modal
      setShowSetupCard(true);
      setShowEditor(false);
      setShowSetupModal(true);
      history.replace({
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
        state: {},
      });
    }
  }, [initialized, history, location]);

  useEffect(() => {
    if (!customizingOrgId || !orgDetail || modalHydrated) return;

    const schedule = orgDetail.usesPersonalDefaults
      ? scheduleFromDefaults(orgDetail.personalDefaults)
      : scheduleFromApi(orgDetail.stored, orgDetail.stored);

    setModalDraft(cloneSchedule(schedule));
    setModalUseDefaults(orgDetail.usesPersonalDefaults);
    setModalHydrated(true);
  }, [customizingOrgId, orgDetail, modalHydrated]);

  const personalDefaults: PreferenceSchedule = useMemo(
    () => ({
      channels,
      deliveryMode,
      summaryTime,
      summaryFrequency,
      summaryDayOfWeek,
      timezone,
    }),
    [
      channels,
      deliveryMode,
      summaryTime,
      summaryFrequency,
      summaryDayOfWeek,
      timezone,
    ]
  );

  const isDirty = useMemo(() => {
    if (!baselineSchedule) return false;
    return (
      !schedulesEqual(personalDefaults, baselineSchedule) ||
      !externalMapsEqual(orgExternalById, baselineOrgExternal)
    );
  }, [personalDefaults, baselineSchedule, orgExternalById, baselineOrgExternal]);

  const orgRows: OrganizationPreferenceRow[] = useMemo(
    () =>
      (overview?.organizations ?? []).map((org) => {
        const id = String(org.organizationId);
        const externalEnabled =
          orgExternalById[id] ?? org.externalNotificationsEnabled;
        return {
          id,
          name: org.organizationName,
          externalEnabled,
          hasOverride: !org.usesPersonalDefaults,
          statusLabel: org.statusLabel,
        };
      }),
    [overview, orgExternalById]
  );

  const canSave =
    isDirty && channels.length > 0 && !isSavingAll && !isLoading;

  async function handleSave({ fromSetupModal = false } = {}) {
    if (channels.length === 0) {
      toastMessages.error("Select at least one communication channel.");
      return;
    }

    // Snapshot before any refetch can race with local UI state.
    const desiredExternal = { ...orgExternalById };
    const serverOrgsSnapshot = [...(overview?.organizations ?? [])];
    const savedSchedule = cloneSchedule(personalDefaults);

    setIsSavingAll(true);
    try {
      await saveDefaultsMutation.mutateAsync({
        payload: toSaveDefaultsPayload(personalDefaults),
        skipInvalidate: true,
      });

      const externalUpdates = serverOrgsSnapshot.filter((org) => {
        const id = String(org.organizationId);
        const localEnabled = desiredExternal[id];
        return (
          localEnabled !== undefined &&
          localEnabled !== org.externalNotificationsEnabled
        );
      });

      for (const org of externalUpdates) {
        await setExternalMutation.mutateAsync({
          organizationId: String(org.organizationId),
          enabled: desiredExternal[String(org.organizationId)],
          skipInvalidate: true,
        });
      }

      await queryClient.refetchQueries({
        queryKey: ["communicationPreferencesOverview"],
      });

      // Keep the toggles the user just saved — do not re-hydrate from a
      // partial/stale overview response mid-flight.
      setOrgExternalById(desiredExternal);
      setBaselineSchedule(savedSchedule);
      setBaselineOrgExternal(desiredExternal);

      await completeMutation.mutateAsync();
      setShowSetupModal(false);
      if (fromSetupModal) {
        // Stay on the Stay informed landing — do not switch to the editor.
        setShowSetupCard(true);
        setShowEditor(false);
      } else {
        setShowSetupCard(false);
        setShowEditor(true);
      }
      toastMessages.success("Preferences saved successfully.");
    } catch (error) {
      toastMessages.error(
        getApiErrorMessage(error, "Failed to save preferences.")
      );
    } finally {
      setIsSavingAll(false);
    }
  }

  function handleCancel() {
    if (!baselineSchedule) return;
    setChannels([...baselineSchedule.channels]);
    setDeliveryMode(baselineSchedule.deliveryMode);
    setSummaryTime(baselineSchedule.summaryTime);
    setSummaryFrequency(baselineSchedule.summaryFrequency);
    setSummaryDayOfWeek(baselineSchedule.summaryDayOfWeek);
    setTimezone(baselineSchedule.timezone);
    setOrgExternalById({ ...baselineOrgExternal });
  }

  function openCustomize(orgId: string) {
    setCustomizingOrgId(orgId);
    setModalDraft(null);
    setModalHydrated(false);
    setModalUseDefaults(false);
  }

  function handleToggleExternal(orgId: string, enabled: boolean) {
    setOrgExternalById((prev) => ({
      ...prev,
      [orgId]: enabled,
    }));
  }

  async function skipSetupModal() {
    setShowSetupModal(false);
  }

  function enterEditorFromSetup() {
    void completeMutation.mutateAsync();
    setShowSetupModal(false);
    setShowSetupCard(false);
    setShowEditor(true);
  }

  function openPreferencesEditor() {
    setShowSetupModal(false);
    setShowSetupCard(false);
    setShowEditor(true);
  }

  async function saveCustomize() {
    if (!customizingOrgId || !modalDraft) return;
    if (!modalUseDefaults && modalDraft.channels.length === 0) {
      toastMessages.error("Select at least one communication channel.");
      return;
    }

    try {
      if (modalUseDefaults) {
        await restoreOrgMutation.mutateAsync(customizingOrgId);
      } else {
        const externalEnabled =
          orgExternalById[customizingOrgId] ??
          overview?.organizations.find(
            (org) => String(org.organizationId) === customizingOrgId
          )?.externalNotificationsEnabled ??
          true;

        await saveOrgMutation.mutateAsync({
          organizationId: customizingOrgId,
          payload: toSaveOrganizationPayload({
            usesPersonalDefaults: false,
            externalNotificationsEnabled: externalEnabled,
            schedule: modalDraft,
          }),
        });
      }

      toastMessages.success("Preferences saved successfully.");
      setCustomizingOrgId(null);
      setModalDraft(null);
      setModalHydrated(false);
    } catch (error) {
      toastMessages.error(
        getApiErrorMessage(error, "Failed to save organization preferences.")
      );
    }
  }

  const customizingOrgName =
    overview?.organizations.find(
      (org) => String(org.organizationId) === customizingOrgId
    )?.organizationName ?? "Organization";

  const modalContact = {
    email: orgDetail?.contact?.email ?? profile?.user?.email,
    mobilePhone: displayPhone,
    fax: profile?.user?.fax,
    mobilePhoneVerified:
      orgDetail?.contact?.mobilePhoneVerified ??
      phoneStatus?.mobilePhoneVerified,
    hasFaxNumber: orgDetail?.contact?.hasFaxNumber,
  };

  const modalPersonalDefaults = orgDetail
    ? scheduleFromDefaults(orgDetail.personalDefaults)
    : personalDefaults;

  const modalSaving =
    saveOrgMutation.isPending || restoreOrgMutation.isPending;

  if (isLoading) {
    return <NotificationSettingsTableLoadingIndicator />;
  }

  return (
    <Fragment>
      <RouteLeavingGuard
        when={isDirty && !isSavingAll && showEditor}
        navigate={(path) => {
          history.push(path);
        }}
        shouldBlockNavigation={() => isDirty && !isSavingAll && showEditor}
        title="Unsaved changes"
        message="Any unsaved changes will be discarded. Are you sure you want to leave?"
      />
      <SubHeader
        title="My preferences"
        hideSubHeaderSelectors
        showButtonInSubheaderForMobile
        containerClass="md:flex"
        customButtons={
          showEditor
            ? [
                {
                  textInformation: {
                    text: "Cancel",
                    fontWeight: "font-semibold",
                  },
                  onClick: handleCancel,
                  buttonTheme: "secondary",
                  className: "my-2 mx-2",
                  disabled: !isDirty || isSavingAll,
                },
                {
                  textInformation: {
                    text: isSavingAll ? "Saving..." : "Save preferences",
                    fontWeight: "font-semibold",
                  },
                  onClick: () => {
                    void handleSave();
                  },
                  buttonTheme: "primary",
                  disabled: !canSave,
                  loading: isSavingAll,
                  className: "my-2 mx-2 md:mr-5",
                },
              ]
            : undefined
        }
      />

      <div className="px-6 py-6 pb-10">
        <p className="text-sm text-textSecondaryGray mb-5">
          Choose how and when you receive notifications across your
          organizations.
        </p>

        {showSetupCard && (
          <FirstVisitSetupCard
            onSetup={openPreferencesEditor}
            onDismiss={enterEditorFromSetup}
          />
        )}

        {showEditor && (
          <Fragment>
            <section className="mb-8">
              <h3 className="text-xl font-bold text-royalBlue mb-1">
                My defaults
              </h3>
              <p className="text-sm text-textSecondaryGray mb-6">
                Applies to all notification types and organizations unless you
                customize an organization.
              </p>

              <div className="grid grid-cols-1 lg:grid-cols-[240px_minmax(0,1fr)] gap-x-10 gap-y-2 mb-8 pb-8 border-b border-dropDownBorder">
                <div>
                  <h4 className="font-semibold text-royalBlue">Channels</h4>
                  <p className="text-sm text-textSecondaryGray mt-1">
                    Where should we send your notifications?
                  </p>
                </div>
                <ChannelSelector
                  channels={channels}
                  onChange={setChannels}
                  email={profile?.user?.email}
                  mobilePhone={displayPhone}
                  fax={profile?.user?.fax}
                  mobilePhoneVerified={phoneStatus?.mobilePhoneVerified}
                  onAddVerifyPhone={() => setShowPhoneModal(true)}
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-[240px_minmax(0,1fr)] gap-x-10 gap-y-2">
                <div>
                  <h4 className="font-semibold text-royalBlue">Delivery</h4>
                  <p className="text-sm text-textSecondaryGray mt-1">
                    One delivery schedule for all notification types.
                  </p>
                </div>
                <DeliveryModeSection
                  deliveryMode={deliveryMode}
                  summaryTime={summaryTime}
                  summaryFrequency={summaryFrequency}
                  summaryDayOfWeek={summaryDayOfWeek}
                  timezone={timezone}
                  onDeliveryModeChange={setDeliveryMode}
                  onSummaryTimeChange={setSummaryTime}
                  onSummaryFrequencyChange={setSummaryFrequency}
                  onSummaryDayOfWeekChange={setSummaryDayOfWeek}
                  onTimezoneChange={setTimezone}
                />
              </div>
            </section>

            <section className="mb-6">
              <h3 className="text-xl font-bold text-royalBlue mb-0.5">
                Your organizations
              </h3>
              <p className="text-sm text-textSecondaryGray mb-4">
                Customize channels or delivery for a specific organization.
              </p>
              <OrganizationsPreferencesList
                organizations={orgRows}
                onToggleExternal={handleToggleExternal}
                onCustomize={openCustomize}
                readOnly={isSavingAll}
              />
            </section>
          </Fragment>
        )}
      </div>

      {showSetupModal && (
        <CommunicationSetupModal
          channels={channels}
          deliveryMode={deliveryMode}
          summaryTime={summaryTime}
          summaryFrequency={summaryFrequency}
          summaryDayOfWeek={summaryDayOfWeek}
          timezone={timezone}
          organizations={orgRows}
          email={profile?.user?.email}
          mobilePhone={displayPhone}
          fax={profile?.user?.fax}
          mobilePhoneVerified={phoneStatus?.mobilePhoneVerified}
          isSaving={isSavingAll}
          onChannelsChange={setChannels}
          onDeliveryModeChange={setDeliveryMode}
          onSummaryTimeChange={setSummaryTime}
          onSummaryFrequencyChange={setSummaryFrequency}
          onSummaryDayOfWeekChange={setSummaryDayOfWeek}
          onTimezoneChange={setTimezone}
          onToggleExternal={handleToggleExternal}
          onCustomize={openCustomize}
          onSave={() => {
            void handleSave({ fromSetupModal: true });
          }}
          onSkip={() => {
            void skipSetupModal();
          }}
          onAddVerifyPhone={() => setShowPhoneModal(true)}
        />
      )}

      {customizingOrgId && (
        <CustomizeOrganizationModal
          organizationName={customizingOrgName}
          draft={modalDraft}
          personalDefaults={modalPersonalDefaults}
          useDefaults={modalUseDefaults}
          email={modalContact.email}
          mobilePhone={modalContact.mobilePhone}
          fax={modalContact.fax}
          mobilePhoneVerified={modalContact.mobilePhoneVerified}
          hasFaxNumber={modalContact.hasFaxNumber}
          isLoading={orgDetailLoading || !modalHydrated || !modalDraft}
          isSaving={modalSaving}
          onChange={(next) => setModalDraft(next)}
          onUseDefaults={() => {
            setModalUseDefaults(true);
            setModalDraft(cloneSchedule(modalPersonalDefaults));
          }}
          onCustomizeInstead={() => setModalUseDefaults(false)}
          onCancel={() => {
            setCustomizingOrgId(null);
            setModalDraft(null);
            setModalHydrated(false);
          }}
          onSave={() => {
            void saveCustomize();
          }}
          onAddVerifyPhone={() => setShowPhoneModal(true)}
        />
      )}

      {showPhoneModal && (
        <PhoneVerificationPopup
          initialPhone={displayPhone}
          onClose={() => setShowPhoneModal(false)}
          onVerified={() => setShowPhoneModal(false)}
        />
      )}
    </Fragment>
  );
}

export default MyPreferencesForm;
